import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { API } from '../api';
import { format, withLanguage } from '../components/common/Texts';

const PASSWORD_MIN_LENGTH = 8;

// Drives the token-based password reset screen: resolves the token to a
// username on mount, validates the new password and persists it.
export default function useResetPassword() {
    const navigate = useNavigate();
    const { token } = useParams();
    const language = useSelector((state) => state.user.language);
    const t = withLanguage(language);

    const [username, setUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');
    const [invalidToken, setInvalidToken] = useState(false);
    const [resolving, setResolving] = useState(true);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let active = true;
        API.get(`/reset-password/${token}/`)
            .then(({ data }) => {
                if (active) setUsername(data.username);
            })
            .catch((e) => {
                console.error(e);
                if (active) setInvalidToken(true);
            })
            .finally(() => {
                if (active) setResolving(false);
            });
        return () => { active = false; };
    }, [token]);

    const submit = async () => {
        if (newPassword.length < PASSWORD_MIN_LENGTH) {
            setError(format(t('reset_password_length'), PASSWORD_MIN_LENGTH));
            return;
        }
        if (newPassword !== repeatPassword) {
            setError(t('reset_password_not_equal'));
            return;
        }

        setError('');
        setLoading(true);
        try {
            await API.post(`/reset-password/${token}/`, { new_password: newPassword });
            navigate('/login');
        } catch (e) {
            console.error(e);
            setError(t('reset_password_error'));
        } finally {
            setLoading(false);
        }
    };

    return {
        username,
        newPassword, setNewPassword,
        repeatPassword, setRepeatPassword,
        invalidToken, resolving, error, loading, submit,
    };
}
